import axios, { AxiosError } from 'axios';
import i18n from '../i18n';
import { authSwitchedError, isAuthSwitchedElsewhere } from '../utils/authTabSync';
import { saveBlobAs } from './api';

// ============================================================================
// P-12 — the customer portal's new endpoints (project page, detailed hour
// report, request actions, customer users) and the OTE-side
// "🔒 לא להציג ללקוח" flag. Kept in its own small client so the shared
// `api.ts` does not change (parallel streams edit it). Same base URL, same
// bearer token, same rejected-error shape (`err.error.message` and
// `err.response.data`) as `api.ts`.
// ============================================================================

const client = axios.create({ baseURL: import.meta.env.VITE_API_URL || '/api', timeout: 30000 });

client.interceptors.request.use((config) => {
  // D-279 (QX-ב): nothing leaves a tab whose user was replaced in another tab.
  if (isAuthSwitchedElsewhere()) return Promise.reject(authSwitchedError(i18n.t('authTabSync.blocked')));
  const token = localStorage.getItem('auth_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
client.interceptors.response.use(
  (r) => r,
  (error: AxiosError<any>) => {
    const body = error.response?.data;
    if (body && typeof body === 'object' && !(body instanceof Blob)) {
      return Promise.reject({
        ...body,
        message: body.error?.message || error.message,
        response: { status: error.response?.status, data: body },
      });
    }
    return Promise.reject(error);
  }
);

type Res<T> = { success: boolean; data: T };
const data = <T>(p: Promise<{ data: T }>): Promise<T> => p.then((r) => r.data);

// ---------------------------------------------------------------- types

export interface PortalTaskNode { id: string; title: string; status: string; progress: number; due_date: string | null }
export interface PortalMilestoneNode {
  id: string; name: string; status: string; due_date: string | null; progress: number;
  tasks: PortalTaskNode[]; subs: PortalMilestoneNode[];
}
export interface PortalProjectListItem {
  id: string; name: string; status: string; kind: string; start_date: string | null; end_date: string | null;
  organization_id: string; organization_name: string; engagement_id: string | null; progress: number;
}
export interface PortalPerson { user_id: string; name: string }
/** T-32: the project page's recipients are the request list (Account Manager + recent task owners). */
export interface PortalRecipient extends PortalPerson { role: 'account_manager' | 'recent_task' }
export interface PortalProjectPage {
  project: {
    id: string; name: string; status: string; kind: string; organization_id: string; organization_name: string;
    start_date: string | null; end_date: string | null; progress: number;
  };
  account_manager: PortalPerson | null;
  project_manager: PortalPerson | null;
  working_now: (PortalPerson & { tasks: { id: string; title: string }[] })[];
  milestones: PortalMilestoneNode[];
  direct_tasks: PortalTaskNode[];
  payments: null | {
    total: number; paid: number; balance: number; currency: string | null;
    /** BQ-068: `number` = the installment's own order (N of `total`); null = another charge (e.g. a server month), not numbered. */
    items: { id: string; number: number | null; title: string; amount: number; currency: string | null; due_date: string | null; payment_status: string; outstanding: number }[];
  };
  pending_signatures: null | { id: string; title: string; link: string }[];
  requests: null | { id: string; ticket_number: number | null; title: string; status: string; opened_at: string; closed_at: string | null; cancelled_at: string | null; requester_name: string | null; message_count: number }[];
  can_create_request: boolean;
  recipients: PortalRecipient[];
}
export interface PortalHourEntry {
  id: string; date: string; engagement_id: string | null; engagement_name: string | null; project_name: string | null;
  task: string | null; task_number: number | null; minutes: number; duration: string; description: string;
  /** P-11 (D-81): a "יחידות AI" row — units only, never USD. */
  kind?: 'ai_units'; ai_units?: number; late_from?: string | null;
  /** D-262 (QX-1242): rejected by the customer - tagged on the list, in no total. */
  rejected?: boolean;
}
export interface PortalHourEntries { rows: PortalHourEntry[]; total_minutes: number; total: string }
export interface PortalUsersConfig {
  enabled: boolean;
  keys: string[];
  organizations: { organization_id: string; organization_name: string; grantable: Record<string, boolean> }[];
}
export interface PortalUserRow {
  grant_id: string; user_id: string; name: string | null; email: string | null; portal_role: string;
  active: boolean; permissions: Record<string, boolean>; approve_tickets: boolean; editable: boolean; created_at: string;
}
export type VisibilityEntity = 'project' | 'milestone' | 'task';

// P-19b (D-145 / D-146)
export interface PortalNewRequestBody {
  organization_id?: string;
  title: string;
  description?: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  project_id?: string;
  recipient_user_id?: string;
}
export interface PortalCreatedRequest {
  id: string;
  ticket_number?: number | null;
  title: string;
  organization_name?: string | null;
  pending_approval: boolean;
}
export interface PortalRequestRecipientOption { user_id: string; name: string; role: 'account_manager' | 'recent_task' }
export interface PortalStaffUpdate {
  kind: 'reply' | 'completed' | 'status' | 'on_it' | 'conversation_started';
  request_id: string;
  ticket_number: number | null;
  title: string;
  author_name: string | null;
  excerpt: string | null;
  at: string;
}

/** Uploads (recordings up to 50MB) get 5 minutes instead of the 30s default. */
const UPLOAD = { timeout: 300_000 };

/** Fields + files as multipart (empty / undefined fields are left out). */
function toForm(body: object, files: File[]): FormData {
  const fd = new FormData();
  for (const [k, v] of Object.entries(body)) {
    if (v !== undefined && v !== null && v !== '') fd.append(k, String(v));
  }
  for (const f of files) fd.append('files', f, f.name);
  return fd;
}

// ---------------------------------------------------------------- calls

export const portalP12Api = {
  listProjects: (params?: { organization_id?: string }) =>
    data<Res<PortalProjectListItem[]>>(client.get('/portal/projects', { params })),
  getProject: (id: string) => data<Res<PortalProjectPage>>(client.get(`/portal/projects/${id}`)),
  /** P-19b: with `files` (file / voice / screen recording) the body goes as multipart. */
  sendProjectRequest: (id: string, body: { title: string; description?: string; priority?: string; recipient_user_id?: string | null }, files: File[] = []) =>
    data<Res<{ id: string; pending_approval: boolean }>>(client.post(`/portal/projects/${id}/requests`, files.length ? toForm(body, files) : body, UPLOAD)),

  // ---- P-19b (D-145 / D-146) ----
  /** Open a request (JSON fields + up to 5 files). */
  createRequest: (body: PortalNewRequestBody, files: File[] = []) =>
    data<Res<PortalCreatedRequest>>(client.post('/portal/requests', toForm(body, files), UPLOAD)),
  /** Whom a request may be sent to ("ל<שם>"): the Account Manager + recent assignees. */
  listRecipients: (organization_id?: string) =>
    data<Res<PortalRequestRecipientOption[]>>(client.get('/portal/requests/recipients', { params: organization_id ? { organization_id } : {} })),
  /** New staff replies since `since` (the toast poll). No `since` = only the server clock. */
  staffUpdates: (since?: string | null) =>
    data<Res<{ server_time: string; items: PortalStaffUpdate[] }>>(client.get('/portal/requests/updates', { params: since ? { since } : {} })),
  async downloadApprovalAttachment(approvalId: string, index: number, name: string): Promise<void> {
    const r = await client.get(`/portal/ticket-approvals/${approvalId}/attachments/${index}`, { responseType: 'blob' });
    saveBlobAs(r.data as Blob, name);
  },

  hourEntries: (params: { organization_id?: string; month?: string; engagement_id?: string }) =>
    data<Res<PortalHourEntries>>(client.get('/portal/hours/entries', { params })),
  async exportHourEntries(format: 'csv' | 'pdf', params: { organization_id?: string; month?: string; engagement_id?: string }): Promise<void> {
    const r = await client.get('/portal/hours/entries/export', { params: { ...params, format }, responseType: 'blob' });
    saveBlobAs(r.data as Blob, `hours-${params.month || 'all'}.${format}`);
  },

  commentOnRequest: (id: string, content: string, files: File[]) => {
    const fd = new FormData();
    fd.append('content', content);
    for (const f of files) fd.append('files', f);
    return data<Res<{ id: string }>>(client.post(`/portal/requests/${id}/comments`, fd, UPLOAD));
  },
  closeRequest: (id: string, reason?: string) =>
    data<Res<{ id: string; status: string }>>(client.post(`/portal/requests/${id}/close`, { reason: reason || null })),
  /** P-59a: the same endpoint (and permission check) as the download, read for inline viewing. */
  async loadAttachment(requestId: string, attachmentId: string): Promise<Blob> {
    const r = await client.get(`/portal/requests/${requestId}/attachments/${attachmentId}`, { responseType: 'blob' });
    return r.data as Blob;
  },
  async downloadAttachment(requestId: string, attachmentId: string, name: string): Promise<void> {
    const r = await client.get(`/portal/requests/${requestId}/attachments/${attachmentId}`, { responseType: 'blob' });
    saveBlobAs(r.data as Blob, name);
  },

  usersConfig: () => data<Res<PortalUsersConfig>>(client.get('/portal/users/config')),
  listUsers: (organization_id?: string) =>
    data<Res<PortalUserRow[]>>(client.get('/portal/users', { params: organization_id ? { organization_id } : {} })),
  addUser: (body: { organization_id?: string; name: string; email: string; password: string; permissions: Record<string, boolean>; approve_tickets: boolean }) =>
    data<Res<PortalUserRow> & { email_sent?: boolean; account_manager_notified?: boolean }>(client.post('/portal/users', body)),
  updateUser: (grantId: string, body: { organization_id?: string; permissions?: Record<string, boolean>; approve_tickets?: boolean; active?: boolean }) =>
    data<Res<PortalUserRow>>(client.patch(`/portal/users/${grantId}`, body)),

  getVisibility: (entity: VisibilityEntity, id: string) =>
    data<Res<{ hidden_from_customer: boolean; can_edit: boolean }>>(client.get(`/customer-visibility/${entity}/${id}`)),
  setVisibility: (entity: VisibilityEntity, id: string, hidden: boolean) =>
    data<Res<{ hidden_from_customer: boolean }>>(client.put(`/customer-visibility/${entity}/${id}`, { hidden })),
};
